import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useDriverStore } from '../../store/driverStore';
import { useAlertManager } from '../../components/AlertProvider';
import { MaterialCommunityIcons } from '../../utils/icons';
import { Colors } from '../../theme/colors';
import './DriverProfileScreen.css';

const DriverProfileScreen: React.FC = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlertManager();
  const { user, logout, uploadProfilePicture } = useAuthStore();
  const { status } = useDriverStore();
  const [uploading, setUploading] = useState(false);

  const handleLogout = () => {
    showAlert('Logout', 'Are you sure you want to logout?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Logout',
        style: 'destructive',
        onPress: async () => {
          await logout();
          navigate('/login');
        },
      },
    ]);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;


    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      showAlert('Error', 'Please select a valid image file (JPEG, PNG, GIF, or WebP)', [{ text: 'OK' }]);
      return;
    }


    const maxSize = 5 * 1024 * 1024; 
    if (file.size > maxSize) {
      showAlert('Error', 'Image size must be less than 5MB', [{ text: 'OK' }]);
      return;
    }

    setUploading(true);
    try {
      await uploadProfilePicture(file);
      showAlert('Success', 'Profile picture uploaded successfully', [{ text: 'OK' }]);
    } catch (error: any) {
      console.error('Profile picture upload error:', error);
      showAlert('Error', error.message || 'Failed to upload profile picture', [{ text: 'OK' }]);
    } finally {
      setUploading(false);

      e.target.value = '';
    }
  };

  const menuItems = [
    { icon: 'folder', label: 'Documents', screen: '/documents', color: Colors.info },
    { icon: 'directions-car', label: 'Vehicles', screen: '/vehicles', color: Colors.primary },
    { icon: 'attach-money', label: 'Earnings', screen: '/earnings', color: Colors.success },
    { icon: 'star', label: 'Ratings', screen: '/ratings', color: Colors.warning },
  ];

  return (
    <div className="profile-container">
      <div className="profile-header">
        <button onClick={() => navigate('/')}><MaterialCommunityIcons name="arrow-back" size={24} /></button>
        <h1>Profile</h1>
      </div>
      <div className="profile-content">
        <div className="profile-section">
          <div className="avatar-container">
            <div className="avatar">
              {user?.profile_image ? (
                <img 
                  src={user.profile_image} 
                  alt={user?.name || 'Driver'} 
                  className="avatar-image"
                  onError={(e) => {

                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent) {
                      const initial = document.createElement('div');
                      initial.className = 'avatar-initial';
                      initial.textContent = user?.name?.charAt(0).toUpperCase() || 'D';
                      parent.appendChild(initial);
                    }
                  }}
                />
              ) : (
                <div className="avatar-initial">
                  {user?.name?.charAt(0).toUpperCase() || 'D'}
                </div>
              )}
            </div>
            <label className="avatar-upload-label" htmlFor="profile-image-input">
              <MaterialCommunityIcons 
                name="camera" 
                size={20} 
                color={Colors.white} 
              />
            </label>
            <input
              id="profile-image-input"
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              onChange={handleImageUpload}
              disabled={uploading}
              style={{ display: 'none' }}
            />
            {uploading && (
              <div className="upload-overlay">
                <div className="upload-spinner"></div>
              </div>
            )}
          </div>
          {status && (
            <span className={`verification-status ${status.verification_status}`}>
              {status.verification_status?.toUpperCase()}
            </span>
          )}
          <h2>{user?.name}</h2>
          <p><MaterialCommunityIcons name="email" size={16} /> {user?.email}</p>
          <p><MaterialCommunityIcons name="phone" size={16} /> {user?.phone}</p>
        </div>
        <div className="menu-section">
          {menuItems.map((item, index) => (
            <button key={index} className="menu-item" onClick={() => navigate(item.screen)}>
              <MaterialCommunityIcons name={item.icon} size={22} color={item.color} />
              <span>{item.label}</span>
              <MaterialCommunityIcons name="chevron-right" size={24} color="#ccc" />
            </button>
          ))}
        </div>
        <button className="logout-button" onClick={handleLogout}>
          <MaterialCommunityIcons name="logout" size={20} color="white" />
          Logout
        </button>
      </div>
    </div>
  );
};

export default DriverProfileScreen;

